// The ground-view enemy's tactical brain — ported from adventure-craft
// `packages/rpg-combat/src/arena/ArenaAIService.ts` (phase D of the AOD
// Studio arc; docs/aod-studio-design.md §1.3 port table). Pure and
// stateless, zero adventure-craft imports. The port's one behavioural
// change is the reason this file exists: the original's 'random' target
// priority reached for `Math.random()` (ArenaAIService.ts:106, the one
// call the spec calls out) — here every decision is a pure function of
// its inputs plus an *injected* `Rng`, so a seed replays a fight exactly.
//
// The arena ability surface is trimmed to what a guard band needs: each
// class has a basic and a special ability with a range and a shared
// cooldown (the original's per-ability cooldown map and ultimates arrive
// with whatever later phase wants boss encounters).

import { type Vec3, distance3D, moveAway, moveToward } from './arenaMovement.js';
import type { Rng } from './rng.js';

/** How an enemy carries itself. Mirrors the original's `AIBehavior`. */
export type AiBehavior = 'aggressive' | 'tactical' | 'defensive';
export type AiTargetPriority = 'nearest' | 'weakest' | 'strongest' | 'random';

/** The three guard archetypes the ground view musters. */
export type GuardClass = 'grunt' | 'archer' | 'captain';

export interface EnemyUnit {
  readonly id: string;
  readonly guardClass: GuardClass;
  readonly behavior: AiBehavior;
  position: Vec3;
  hp: number;
  readonly maxHp: number;
  isAlive: boolean;
  /** Shared ability cooldown, in seconds of game time. */
  cooldownRemaining: number;
}

export interface ArenaTarget {
  readonly id: string;
  position: Vec3;
  hp: number;
  readonly maxHp: number;
  isAlive: boolean;
}

export interface GuardAbility {
  readonly id: string;
  readonly range: number;
  /** Presentation weight — the client picks a wind-up by it. */
  readonly windUpSeconds: number;
}

export interface AiDecision {
  /** Move to this position (null = hold). */
  readonly moveTarget: Vec3 | null;
  /** The ability to use (null = reposition only). */
  readonly abilityId: string | null;
  readonly targetId: string | null;
}

interface ClassKit {
  readonly basic: GuardAbility;
  readonly special: GuardAbility;
  /** Seconds a class waits between abilities. */
  readonly cooldownSeconds: number;
}

const KITS: Record<GuardClass, ClassKit> = {
  // The melee rank-and-file: short reach, cheap special.
  grunt: {
    basic: { id: 'club_swipe', range: 1.9, windUpSeconds: 0.35 },
    special: { id: 'shield_bash', range: 2.4, windUpSeconds: 0.5 },
    cooldownSeconds: 4,
  },
  // Keeps distance; the defensive behavior's kite pattern is its fight.
  archer: {
    basic: { id: 'loose_arrow', range: 6.5, windUpSeconds: 0.6 },
    special: { id: 'aimed_shot', range: 8, windUpSeconds: 0.9 },
    cooldownSeconds: 6,
  },
  // The band's leader: fights like a grunt with a longer reach.
  captain: {
    basic: { id: 'sword_cut', range: 2.4, windUpSeconds: 0.4 },
    special: { id: 'captains_mark', range: 3.0, windUpSeconds: 0.7 },
    cooldownSeconds: 5,
  },
};

export function kitFor(guardClass: GuardClass): ClassKit {
  return KITS[guardClass];
}

/**
 * Full tactical decision for one enemy. Behavior → target priority →
 * ability → movement, exactly the original's pipeline.
 */
export function decide(enemy: EnemyUnit, targets: readonly ArenaTarget[], rng: Rng): AiDecision {
  const idle: AiDecision = { moveTarget: null, abilityId: null, targetId: null };
  const alive = targets.filter((t) => t.isAlive);
  if (!enemy.isAlive || alive.length === 0) return idle;

  const target = selectTarget(enemy, alive, rng);
  if (!target) return idle;

  const ability = selectAbility(enemy, target);
  const moveTarget = decideMovement(enemy, target, ability);
  return { moveTarget, abilityId: ability?.id ?? null, targetId: target.id };
}

export function selectTarget(
  enemy: EnemyUnit,
  targets: readonly ArenaTarget[],
  rng: Rng,
): ArenaTarget | null {
  if (targets.length === 0) return null;
  switch (targetPriority(enemy.behavior)) {
    case 'nearest':
      return findNearest(enemy.position, targets);
    case 'weakest':
      return findWeakest(targets);
    case 'strongest':
      return findStrongest(targets);
    case 'random':
      // The injected RNG replaces ArenaAIService.ts:106's Math.random().
      return targets[Math.floor(rng() * targets.length)]!;
  }
}

export function selectAbility(enemy: EnemyUnit, target: ArenaTarget): GuardAbility | null {
  const kit = kitFor(enemy.guardClass);
  const dist = distance3D(enemy.position, target.position);

  // Tactical enemies save the special for a wounded target; everyone
  // else spends it the moment it is ready and in range.
  if (enemy.cooldownRemaining <= 0 && dist <= kit.special.range) {
    if (enemy.behavior === 'tactical') {
      if (target.hp / target.maxHp < 0.4) return kit.special;
    } else {
      return kit.special;
    }
  }
  if (dist <= kit.basic.range) return kit.basic;
  return null;
}

export function decideMovement(
  enemy: EnemyUnit,
  target: ArenaTarget,
  ability: GuardAbility | null,
): Vec3 | null {
  const dist = distance3D(enemy.position, target.position);
  const desiredRange = ability?.range ?? 2.0;

  switch (enemy.behavior) {
    case 'aggressive':
      return dist > desiredRange ? moveToward(enemy.position, target.position, 1) : null;
    case 'tactical':
      // Hold the optimal band: close in when far, give ground when crowded.
      if (dist > desiredRange * 1.2) return moveToward(enemy.position, target.position, 1);
      if (dist < desiredRange * 0.5) return moveAway(enemy.position, target.position, 1);
      return null;
    case 'defensive':
      // Retreat when hurt and pressed; otherwise edge to range.
      if (enemy.hp / enemy.maxHp < 0.3 && dist < 5) {
        return moveAway(enemy.position, target.position, 2);
      }
      return dist > desiredRange ? moveToward(enemy.position, target.position, 0.5) : null;
  }
}

function targetPriority(behavior: AiBehavior): AiTargetPriority {
  switch (behavior) {
    case 'aggressive':
      return 'nearest';
    case 'tactical':
      return 'weakest';
    case 'defensive':
      return 'nearest';
  }
}

function findNearest(pos: Vec3, targets: readonly ArenaTarget[]): ArenaTarget {
  let best = targets[0]!;
  let bestDist = distance3D(pos, best.position);
  for (let i = 1; i < targets.length; i++) {
    const d = distance3D(pos, targets[i]!.position);
    if (d < bestDist) {
      best = targets[i]!;
      bestDist = d;
    }
  }
  return best;
}

function findWeakest(targets: readonly ArenaTarget[]): ArenaTarget {
  let best = targets[0]!;
  for (let i = 1; i < targets.length; i++) {
    if (targets[i]!.hp < best.hp) best = targets[i]!;
  }
  return best;
}

function findStrongest(targets: readonly ArenaTarget[]): ArenaTarget {
  let best = targets[0]!;
  for (let i = 1; i < targets.length; i++) {
    if (targets[i]!.hp > best.hp) best = targets[i]!;
  }
  return best;
}
